import { LightOutlineLinkButton } from 'components/Buttons';
import { Switch } from 'components/Forms/Switch';
import { Skeleton } from 'components/Skeleton';
import Constants from 'config/Constants';
import { ROUTES } from 'config/routes';
import { useGetAreaBaseBlocksQuery, useGetAreaBlocksQuery } from 'features/documents/documents';
import { useUserProfile } from 'hooks/useUserProfile';
import { DateTime } from 'luxon';
import { useGetApprovableTarget } from 'pages/approvals/hooks/useGetApprovableTarget';
import { Block } from 'pages/approvals/modals/ApprovalItems/Block';
import { actions } from 'pages/documents_v2/config/views';
import { useGetArea } from 'pages/documents_v2/hooks/useGetArea';
import { useGetBaseVariant } from 'pages/documents_v2/hooks/useGetBaseVariant';
import { useGetDocumentUser } from 'pages/documents_v2/hooks/useGetDocumentUser';
import { useGetDocumentVariants } from 'pages/documents_v2/hooks/useGetDocumentVariants';
import { VariantsAsLabels } from 'pages/documents_v2/views/edit_area/blocks/VariantsAsLabels';
import { useMemo, useState } from 'react';
import { BoxArrowUpRight, ChevronRight, InfoSquare } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { generatePath, useRouteMatch } from 'react-router-dom';

/**
 * @param {import('models/approval.models').ApprovalItem} approvalItem
 * @param {import('models/approval.models').ApprovalSnapshot} [snapshot]
 */
export function BlockItem({ approvalItem, snapshot }) {
    const userProfile = useUserProfile();
    const { t } = useTranslation();
    const [hideChanges, setHideChanges] = useState(false);
    const target = useGetApprovableTarget(approvalItem.target.id);
    const areaId = target?.parent ? target.parent.externalId : undefined;
    const area = useGetArea(areaId);
    const baseVariant = useGetBaseVariant(area?.documentId);
    const baseBlocks = useGetAreaBaseBlocks(area?.id, baseVariant?.id, snapshot !== undefined);
    const documentUser = useGetDocumentUser(userProfile.userId, area?.documentId);

    const blockId = parseInt(approvalItem.target.externalId);

    const baseBlock = useMemo(() => {
        return baseBlocks.find((block) => block.id === blockId);
    }, [baseBlocks, blockId]);

    const baseBlocksHasChanges = baseBlocks ? baseBlocks.some((baseBlock) => baseBlock.changes.length > 0) : false;

    const editAreaMatch = useRouteMatch(ROUTES.documents.editArea);
    const showLinkToAreaBtn = editAreaMatch === null && documentUser;

    const sectionTarget = approvalItem.target.parent.parent;
    const documentTarget = sectionTarget.parent;

    return (
        <>
            <div className="article-header flex-center justify-content-between">
                <div>
                    <div className="font-weight-bolder" style={{ fontSize: 16 }}>
                        {area?.title}
                    </div>
                    <div className="flex-center text-muted">
                        {documentTarget.name}
                        <ChevronRight className="mx-1" />
                        {sectionTarget.name}
                    </div>
                </div>

                <div className="flex-center">
                    {((baseBlock && baseBlock.changes.length > 0) ||
                        (baseBlock?.type === 'blockLayout' && baseBlocksHasChanges)) && (
                        <Switch
                            label={t('documents:document.navbar.main.editor.left.blocks.highlightChanges')}
                            name="block-item-show-changes-switch"
                            checked={!hideChanges}
                            onChange={() => setHideChanges(!hideChanges)}
                        />
                    )}

                    {showLinkToAreaBtn && (
                        <LightOutlineLinkButton
                            size="sm"
                            target="_blank"
                            className="d-flex align-items-baseline ml-3"
                            to={generatePath(ROUTES.documents.editArea, {
                                documentId: area?.documentId ?? 0,
                                view:
                                    documentUser.role === Constants.userDocumentRole.spectator
                                        ? actions.VIEW
                                        : actions.EDIT,
                                areaId: area?.id ?? 0,
                            })}
                        >
                            {t('ai:blockItem.viewArticle')}
                            <BoxArrowUpRight size={11} className="mr-0 ml-1" />
                        </LightOutlineLinkButton>
                    )}
                </div>
            </div>

            <RenderBlock
                area={area}
                blockId={blockId}
                baseVariant={baseVariant}
                baseBlock={baseBlock}
                baseBlocks={baseBlocks}
                hideChanges={hideChanges}
                snapshot={snapshot}
            />
        </>
    );
}

function RenderBlock({ blockId, area, baseVariant, baseBlock, baseBlocks, snapshot, hideChanges }) {
    const { t, i18n } = useTranslation();
    const areaData = useGetAreaData(area?.id, baseVariant?.id, snapshot !== undefined);
    const allAreaBlocks = areaData.areaBlocks;

    const areaBlocks = useMemo(() => {
        if (snapshot) {
            return snapshot?.content?.areaBlocks ?? [];
        }

        return allAreaBlocks;
    }, [snapshot, allAreaBlocks]);

    const block = useMemo(() => {
        if (snapshot) {
            return snapshot.content.block;
        }

        return areaBlocks.find((block) => block.id === blockId);
    }, [areaBlocks, blockId, snapshot]);

    if (area && areaBlocks.length > 0 && !block) {
        return (
            <div className="alert alert-danger mt-4">
                Kan de tekst van het blok niet vinden. Controleer of het blok in het artikel zit en of het niet
                verwijderd is.
            </div>
        );
    }

    if (!area || !block) {
        return (
            <div className="edit-area-block edit-area-block-type-text read-only p-4 mt-3">
                <Skeleton count={3} />
            </div>
        );
    }

    return (
        <div>
            <div className={`edit-area-block edit-area-block-type-${block.type} read-only mb-0 border-0 ml-n4 mr-n4`}>
                <Block
                    block={block}
                    baseBlock={baseBlock}
                    areaId={area.id}
                    documentId={area.documentId}
                    areaBlocks={areaBlocks}
                    baseBlocks={baseBlocks}
                    hideChanges={hideChanges}
                />
            </div>

            {snapshot && (
                <div className="small text-muted mb-3">
                    <InfoSquare className="mr-1" />
                    {t('ai:blockItem.snapshotInfo', { date: renderDate(snapshot.createdAt) })}
                </div>
            )}

            <BlockVariants area={area} block={block} />
        </div>
    );

    function renderDate(createdAt) {
        return DateTime.fromISO(createdAt).setLocale(i18n.language).toLocaleString(DateTime.DATETIME_MED);
    }
}

function BlockVariants({ area, block }) {
    const blockVariants = block.documentVariants ?? emptyArray;
    const documentVariants = useGetDocumentVariants(area.documentId);

    return (
        <VariantsAsLabels
            blockVariants={blockVariants}
            areaVariants={area.documentVariants ?? []}
            documentVariants={documentVariants}
            showLimit={3}
            toggleEnabled={false}
        />
    );
}

const emptyArray = [];

/**
 * @param {number} [areaId]
 * @param {number} [variantId]
 * @param {boolean} skip
 *
 * @returns {{
 *     addedToVariant: boolean,
 *     area: import('models/documents.models').Area
 *     areaBlocks: import('models/documents.models').AreaBlock[]
 *     removedFromVariant: boolean,
 *     variant: number
 * }}
 */
function useGetAreaData(areaId, variantId, skip) {
    const { areaData } = useGetAreaBlocksQuery(
        { areaId, variantId },
        {
            selectFromResult: ({ currentData, isUninitialized, isFetching }) => ({
                areaData: currentData ?? {
                    id: undefined,
                    areaBlocks: emptyArray,
                },
                isLoading: isUninitialized || (isFetching && !currentData),
            }),
            skip: areaId === undefined || variantId === undefined || skip,
        },
    );

    return areaData;
}

/**
 * @param {number} [areaId]
 * @param {number} [variantId]
 * @param {boolean} skip
 *
 * @returns {import('models/documents.models').BaseBlock[]}
 */
function useGetAreaBaseBlocks(areaId, variantId, skip) {
    const { baseBlocks } = useGetAreaBaseBlocksQuery(
        { areaId, variantId },
        {
            selectFromResult: ({ currentData }) => {
                const items = currentData ?? emptyArray;

                return {
                    baseBlocks: items,
                };
            },
            skip: areaId === undefined || variantId === undefined || skip,
        },
    );

    return baseBlocks;
}
